import React, { CSSProperties, ReactNode, useContext, useState, useEffect, useMemo } from 'react';
import NewProjectDialog from '../../../components/dialogs/NewProjectDialog/NewProjectDialog';
import { ProjectSortInput, Tag, UserRoleEnum, useGetProjectsQuery, useGetDefaultQuery, useGetClientProjectsSummaryQuery } from '../../../generated/graphql';
import { ClientContext } from '../Clients.Provider';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import DeleteProjectsDialog from '../../../components/dialogs/DeleteProjectsDialog/DeleteProjectsDialog';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import { Plus, Trash2, LibraryBig } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import clsx from 'clsx';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type ProjectsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const Projects = (props: ProjectsProps) => {
    const client = useContext(ClientContext);
    const [newProjectVisible, setNewProjectVisible] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [deleteProjectsDialogVisible, setDeleteProjectsDialogVisible] = useState(false);
    const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(null);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const defaultResult = useGetDefaultQuery();
    const isProjSubNameEnabled = defaultResult?.data?.Default?.isProjSubNameEnabled;
    const [toggle, setToggle] = useState(false);

    useEffect(() => {
        setToggle(isProjSubNameEnabled !== undefined ? isProjSubNameEnabled : false);
    }, [isProjSubNameEnabled]);

    const result = useGetProjectsQuery({
        variables: {
            tagIds: tagIds,
            clientId: client?.data?.Client?.id,
            libraryId: selectedLibraryId || undefined,
            contains: searchText,
            order: createGraphQLOrder<ProjectSortInput>(sort),
        }
    });

    // Lightweight query that fetches ALL projects for this client (not paginated)
    const summaryResult = useGetClientProjectsSummaryQuery({
        variables: {
            clientId: client?.data?.Client?.id,
            tagIds: tagIds
        }
    });

    const handleDeleteProjectOnClose = () => {
        setDeleteProjectsDialogVisible(false);
        result.refetch();
        summaryResult.refetch();
    };
    const handleNewProjectDialogOnClose = () => {
        setNewProjectVisible(false);
        result.refetch();
        summaryResult.refetch();
    };
    const handleOnFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    // Derive top 5 libraries by project count from the full (non-paginated) summary
    const topLibraries = useMemo(() => {
        const nodes = summaryResult.data?.ProjectList?.nodes;
        if (!nodes) return [];
        const counts = new Map<string, { id: string; name: string; count: number }>();
        for (const project of nodes) {
            const libraryName = project?.library?.name;
            const libraryId = project?.library?.id ?? project?.libraryId;
            if (!libraryName || !libraryId) continue;
            const existing = counts.get(libraryId);
            if (existing) {
                existing.count++;
            } else {
                counts.set(libraryId, { id: libraryId, name: libraryName, count: 1 });
            }
        }
        return Array.from(counts.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [summaryResult.data]);

    const totalProjects = summaryResult.data?.ProjectList?.nodes?.length ?? 0;

    const cardColors = [
        { color: 'var(--accent)', bgColor: 'var(--accent-soft)' },
        { color: 'var(--success)', bgColor: 'var(--success-soft)' },
        { color: 'var(--info)', bgColor: 'var(--info-soft)' },
        { color: 'var(--warning)', bgColor: 'var(--warning-soft)' },
        { color: '#8b5cf6', bgColor: '#f5f3ff' },
    ];

    const columns = toggle
        ? [
            { id: 'projectSubName', name: 'Sub Name', sortField: 'projectSubName' },
            { id: 'library.name', name: 'Library' },
        ]
        : [
            { id: 'library.name', name: 'Library' },
        ];

    return (
        <div className="flex flex-col h-full">
            <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => result.refetch()} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={client?.data?.Client?.isAdmin || false}>
                    <button className="btn btn-ghost text-sm" onClick={() => setNewProjectVisible(true)}>
                        <Plus size={15} /> New Project
                    </button>
                    {/* Sub-name toggle */}
                    <label className="inline-flex items-center gap-2 ml-2 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                        <span>Sub-name</span>
                        <button
                            onClick={() => setToggle(!toggle)}
                            className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200"
                            style={{ backgroundColor: toggle ? 'var(--accent)' : 'var(--border-strong)' }}
                        >
                            <span
                                className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform duration-200"
                                style={{ transform: toggle ? 'translateX(18px)' : 'translateX(3px)' }}
                            />
                        </button>
                    </label>
                    {selectedIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteProjectsDialogVisible(true)}>
                                <Trash2 size={15} /> Delete
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>

            {/* Top Libraries by Project Count */}
            {topLibraries.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 px-4 py-3">
                    {topLibraries.map((library, index) => {
                        const palette = cardColors[index % cardColors.length];
                        const pct = totalProjects > 0 ? Math.round((library.count / totalProjects) * 100) : 0;
                        const isActive = selectedLibraryId === library.id;
                        return (
                            <div
                                key={library.id}
                                className={clsx('card px-4 py-3 cursor-pointer transition-all', isActive && 'ring-2')}
                                style={isActive ? { borderColor: palette.color, boxShadow: `0 0 0 2px ${palette.color}` } : {}}
                                onClick={() => setSelectedLibraryId(prev => prev === library.id ? null : library.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: palette.bgColor }}
                                    >
                                        <LibraryBig size={16} style={{ color: palette.color }} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{library.count}</div>
                                        <div className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }} title={library.name}>{library.name}</div>
                                    </div>
                                    <span
                                        className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: palette.bgColor, color: palette.color }}
                                    >
                                        {pct}%
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex-1 min-h-0 relative">
                <div className="absolute inset-0">
                <DataTableNew
                    isLoading={result.loading}
                    data={result.data?.ProjectList?.nodes}
                    link="/Projects"
                    columns={columns}
                    filterable
                    onSearchChanged={(value) => setSearchText(value)}
                    onFilterChanged={handleOnFilterChanged}
                    onSelected={(ids) => setSelectedIds(ids)}
                    sortingMode="server"
                    sort={sort}
                    onSortChange={setSort}
                    fetchMore={() => result.data?.ProjectList?.pageInfo?.hasNextPage && result.fetchMore({
                        variables: {
                            libraryId: selectedLibraryId || undefined,
                            clientId: client?.data?.Client?.id,
                            contains: searchText,
                            after: result.data?.ProjectList?.pageInfo.endCursor,
                            order: createGraphQLOrder<ProjectSortInput>(sort),
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                            if (!fetchMoreResult) return previousResult;
                            const previousContents = result.data?.ProjectList?.nodes || [];
                            const newContents = fetchMoreResult.ProjectList?.nodes || [];
                            return {
                                ...previousResult,
                                ProjectList: {
                                    nodes: [...previousContents, ...newContents],
                                    pageInfo: fetchMoreResult.ProjectList?.pageInfo as any
                                }
                            };
                        }
                    })}
                />
                </div>
            </div>

            <NewProjectDialog clientId={client?.data?.Client?.id} visible={newProjectVisible} onClose={handleNewProjectDialogOnClose} />
            <DeleteProjectsDialog ids={selectedIds} visible={deleteProjectsDialogVisible} onClose={handleDeleteProjectOnClose} />
        </div>
    );
};

export default Projects;
